import { defineComponent as _defineComponent } from 'vue'
import BaseCountdownTimer from 'JS/gifts/components/countdown/BaseCountdownTimer.vue';
import Icon from 'JS/components/Icon.vue';
import VIntersectionObserver from 'JS/components/VIntersectionObserver.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { createPromoDataAttributes } from 'JS/utilities/analytics';
import { HeaderAdCountdown, IHeaderAd, IHeaderJson } from 'Src/js/types/Header';
import { usePrefersReducedMotion } from 'JS/composables/prefers-reduced-motion';


export default /*#__PURE__*/_defineComponent({
  __name: 'ScrollingSiteMessenger',
  props: {
    headerData: null
  },
  setup(__props: any) {

const props = __props as {
	headerData: IHeaderJson;
}



const slideContainerId = "scrolling-site-messenger-slides";

const transitionTime = 750;
const pauseTime = 7000;

const prefersReducedMotion = usePrefersReducedMotion();

const currentIndex = ref<number>(0);
const scrollWrapper = ref<HTMLElement | null>(null);

//GA TRACKING START
const viewTracker = ref<number[]>([]);
const scrollViewport = ref<InstanceType<typeof VIntersectionObserver> | null>(null);
const isOnScreen = ref<boolean>();

const messageObserverOptions = computed(()=> {
	return { root: scrollViewport.value?.$el };
});

const scrollerObserverOptions = { rootMargin: '-15px' };

function handleScrollWrapperOnScreen() {
	isOnScreen.value = true;
	trackView(displayedMessages.value[currentIndex.value + 1], currentIndex.value + 1);
}

function shouldHaveGaTracking(message: IHeaderAd) {
	return !!(message?.link ?? false);
}

function countdownDisplay(days: number, hours: number, minutes: number, seconds: number) {
	let hourString = hours.toString().padStart(2, '0');
	const minuteString = minutes.toString().padStart(2, '0');
	const secondString = seconds.toString().padStart(2, '0');

	if (days <= 1) {
		hourString = (hours + (days * 24)).toString().padStart(2, '0');
		return `${hourString}:${minuteString}:${secondString}`;
	} else {
		const dayString = days.toString().padStart(2, '0');

		return `${dayString}:${hourString}:${minuteString}:${secondString}`;
	}
}

function countdownAlly(days: number, hours: number, minutes: number, seconds: number) {
	let dayString = "";
	let hourString = "";
	let minuteString = "";
	let secondString = "";

	if (days !== 0) {
		dayString = `${days} Day${days === 1 ? '' : 's'}, `;
	}

	if (hours !== 0) {
		hourString = `${hours} Hour${hours === 1 ? '' : 's'}, `;
	}

	if (minutes !== 0) {
		minuteString = `${minutes} Minute${minutes === 1 ? '' : 's'}, `;
	}

	if (seconds !== 0) {
		secondString = `${seconds} Second${seconds === 1 ? '' : 's'}`;
	}

	return dayString + hourString + minuteString + secondString + ' left.';
}

function getGaDataAttributes(message: IHeaderAd, idx: number) {
	if (!shouldHaveGaTracking(message)) return undefined;

	return createPromoDataAttributes({
		promoId: message.link.replace("https://www.farmandfleet.com", ""),
		creativeSlot: `scrolling_messages_${normalizeIndex(idx)}`,
		creativeName: "scrolling_messages",
		promoName: `${message.text}`,
	});
};

function trackView(message: IHeaderAd, idx: number) {
	if (!shouldHaveGaTracking(message) || !isOnScreen.value || viewTracker.value.includes(normalizeIndex(idx))) return;

	//Copied from the View Promotion Tag in GTM
	window.blainGtmDataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
	window.blainGtmDataLayer.push({
		'event': 'view_promotion',
		'ecommerce': {
			'items': [
				{
					'promotion_id': message.link.replace("https://www.farmandfleet.com", ""),
					'promotion_name': `${message.text}`,
					'creative_name': "scrolling_messages" + "_" + window.location.pathname,
					'creative_slot': `scrolling_messages_${normalizeIndex(idx)}`,
				},
			]
		}
	});

	viewTracker.value.push(normalizeIndex(idx));
}

function normalizeIndex(idx: number) {
	if (totalMessages.value < 1) return idx;

	if (idx == 0) return displayedMessages.value.length - 2;

	if (idx == displayedMessages.value.length - 1) return 1;

	return idx;
}
//GA TRACKING END

const messages = computed(()=> {
	return props.headerData.headerAds ?? [];
});

const totalMessages = computed(() => messages.value?.length ?? 0);

const displayedMessages = computed(()=> {
	if (totalMessages.value <= 1) {
		return messages.value;
	}

	return [
		messages.value[messages.value.length - 1],
		...messages.value,
		messages.value[0]
	];
});

const ariaLiveText = computed(() => {
	const currentMessage = displayedMessages.value[currentIndex.value + 1];
	if (isAutoScrolling.value || !currentMessage) {
		return null;
	}

	let countdownText = "";

	if (currentMessage.countdown !== HeaderAdCountdown.None) {
		const currentTimeInMS = Date.now();
		const timeDifference = new Date(currentMessage.endDateTime).getTime() - currentTimeInMS;

		if (timeDifference <= 0) {
			countdownText = countdownAlly(0, 0, 0, 0);
		} else {
			const SECOND = 1000;
			const MINUTE = 60 * SECOND;
			const HOUR = 60 * MINUTE;
			const DAY = 24 * HOUR;

			countdownText = countdownAlly(
				Math.floor(timeDifference / DAY),
				Math.floor((timeDifference % DAY) / HOUR),
				Math.floor((timeDifference % HOUR) / MINUTE),
				Math.floor((timeDifference % MINUTE) / SECOND)
			);
		}
	}

	return `Showing slide ${createSlideLabel(currentIndex.value + 1)}: ${currentMessage.text}. ${countdownText}`;
});

function createSlideLabel(index: number): string | undefined {
	if (totalMessages.value === 1) {
		return undefined;
	}

	// makes index 1-indexed, for user facing count and
	// makes the copies on either end match the originals
	if (index === 0) {
		index = totalMessages.value;
	} else if (index > totalMessages.value) {
		index = 1;
	}

	return `${index} of ${totalMessages.value}`;
}

// Scrolling

function handleArrow(direction: 'left' | 'right') {
	disableAutoScroll();
	if (direction === 'left') {
		prev();
	} else {
		next();
	}
}

function scroll(ms: number = transitionTime) {
	const isSlideAction = ms > 0;
	if (prefersReducedMotion.value) {
		ms = 0;
	}

	// Move slider
	scrollWrapper.value.style.transitionDuration = `${ms}ms`;
	scrollWrapper.value.style.transform = `translate3D(${(-currentIndex.value - 1) * 100}%, 0, 0)`;

	// the transitionend event doesn't fire when we force ms to be 0
	// this ensures we end up at the right position when wrapping
	if (isSlideAction && prefersReducedMotion.value) {
		correctWrappedIndex();
	}
}

function prev() {
	if (currentIndex.value <= -1) return; // prevent blanks on fast prev-click
	currentIndex.value -= 1;
	scroll();
}

function next() {
	if (currentIndex.value >= totalMessages.value) return; // prevent blanks on fast next-click
	currentIndex.value += 1;
	scroll();
}

function handleTransitionEnd() {
	correctWrappedIndex();
}

function correctWrappedIndex() {
	if (currentIndex.value <= -1) {
		currentIndex.value = totalMessages.value - 1;
		scroll(0);
	} else if (currentIndex.value >= totalMessages.value) {
		currentIndex.value = 0;
		scroll(0);
	}
}

// Auto-Scrolling

const isAutoScrolling = ref<boolean>(false);
const isAutoScrollDisabled = ref(false);
const isAutoScrollAllowed = computed(() => {
	return totalMessages.value > 1 && !isAutoScrollDisabled.value && !prefersReducedMotion.value;
});

let autoScrollInterval: number | undefined;
function startAutoScroll() {
	if (!isAutoScrollAllowed.value) {
		return;
	}

	stopAutoScroll(); // make sure to clear existing interval if running

	isAutoScrolling.value = true;
	autoScrollInterval = window.setInterval(next, transitionTime + pauseTime);
}

function stopAutoScroll() {
	isAutoScrolling.value = false;
	window.clearInterval(autoScrollInterval);
};

function disableAutoScroll() {
	isAutoScrollDisabled.value = true;
	stopAutoScroll();
}

const hasPointer = ref(false);
const hasFocus = ref(false);

// ensures that autoplay is paused when focus/mouse within container
watch(() => hasPointer.value || hasFocus.value, pause => {
	if (pause) {
		stopAutoScroll();
	} else {
		startAutoScroll();
	}
});

// if no longer allowed, make sure to stop auto scrolling
watch(isAutoScrollAllowed, allowed => {
	if (!allowed) {
		stopAutoScroll();
	}
});

function handleFocusIn(e: FocusEvent) {
	if ((e.currentTarget as HTMLElement).contains(e.target as Node)) {
		hasFocus.value = true;
	};
}

function handleFocusOut(e: FocusEvent) {
	if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
		hasFocus.value = false;
	};
}

function initScrollPosition() {
	currentIndex.value = 0;
	scroll(0);
}

onMounted(()=> {
	if (isAutoScrollAllowed.value) {
		startAutoScroll();
	}

	if (totalMessages.value > 1) {
		initScrollPosition();
	}
});


return { __sfc: true,props, slideContainerId, transitionTime, pauseTime, prefersReducedMotion, currentIndex, scrollWrapper, viewTracker, scrollViewport, isOnScreen, messageObserverOptions, scrollerObserverOptions, handleScrollWrapperOnScreen, shouldHaveGaTracking, countdownDisplay, countdownAlly, getGaDataAttributes, trackView, normalizeIndex, messages, totalMessages, displayedMessages, ariaLiveText, createSlideLabel, handleArrow, scroll, prev, next, handleTransitionEnd, correctWrappedIndex, isAutoScrolling, isAutoScrollDisabled, isAutoScrollAllowed, autoScrollInterval, startAutoScroll, stopAutoScroll, disableAutoScroll, hasPointer, hasFocus, handleFocusIn, handleFocusOut, initScrollPosition, BaseCountdownTimer, Icon, VIntersectionObserver, HeaderAdCountdown }
}

})